/**
 * What the OPEN book is worth against what it was entered at — ROADMAP 9.64.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE FIGURES IN THAT HEADER, AND THEY ANSWER THREE QUESTIONS
 *
 *   DAY MTM    what TODAY did, realised and unrealised together, from the
 *              day journal. It resets at the open and it includes money
 *              already banked.
 *   OPEN MTM   what the positions still HELD are worth against the prices
 *              they were entered at. It spans days, and it is purely
 *              unrealised.
 *   OPEN RISK  what they could still cost at expiry.
 *
 * The middle one was missing, and it is the one a trader checks against
 * their broker's position screen — that figure is open quantity times the
 * distance from entry, and nothing on this page stated it.
 *
 * REALISED IS DELIBERATELY ABSENT. A leg booked an hour ago is no longer a
 * position, and since 9.51 its P&L is attributed to whoever held it and
 * shown on the journal. Folding it in here would mix a closed decision into
 * a figure about what is still at risk, and would double-count against the
 * journal's own total.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS THE SAME FUNCTION EACH CARD USES
 *
 * `basketMtm` over every held leg of every trade, rather than a sum of the
 * cards' own figures. A second implementation of one sum is how a header
 * comes to disagree with the rows beneath it, and `basketMtm`'s tagged
 * union is the whole reason to reuse it: an unmarked leg must not add as
 * zero. A far wing can go minutes without a trade, and the leg carrying the
 * most risk is often the one least likely to be marked — so the total says
 * "12 of 15 marked" rather than quietly understating the book.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { memo, useMemo } from 'react'
import { basketMtm, type MtmLeg } from '@apna/shared'
import { useTickLtps } from '@/feed/useTick'
import { cn } from '@/lib/utils'
import { compactInr } from './quantity'
import type { OpenBookTrade } from './useOpenBook'

/** Signed, compact, and never bare: the sign is the information. */
const money = (v: number): string =>
  `${v < 0 ? '-' : '+'}₹${compactInr(Math.abs(v))}`

export const OpenMtm = memo(function OpenMtm({
  trades,
}: {
  trades: readonly OpenBookTrade[]
}) {
  /*
   * Every held leg of every open trade, flattened. Taken from the SAME
   * `netQty`/`avgPrice` the rows read, so the header cannot drift from the
   * cards under it.
   */
  const legs = useMemo(
    () => trades.flatMap((t) => t.legs.filter((l) => l.netQty !== 0)),
    [trades],
  )
  const tokens = useMemo(
    () => [
      ...new Set(
        legs.map((l) => l.token).filter((t): t is number => t !== null),
      ),
    ],
    [legs],
  )
  const ltps = useTickLtps(tokens)

  const { unrealised } = useMemo(
    () =>
      basketMtm(
        legs.map(
          (l): MtmLeg => ({
            netQty: l.netQty,
            avgPrice: l.avgPrice,
            // Absent means no tick, which must stay distinct from zero all
            // the way into the sum — see `basketMtm`.
            ltp: l.token === null ? null : (ltps.get(l.token) ?? null),
            // 9.64. Unrealised ONLY. The booked half lives on the journal.
            realisedPnl: 0,
            chargesTotal: 0,
          }),
        ),
      ),
    [legs, ltps],
  )

  if (legs.length === 0) return null

  return (
    <div className="tr-stat" data-testid="open-mtm">
      <span
        className="tr-flabel"
        title={
          'Open positions only, at their entry prices. Unrealised — booked P&L is on the journal. ' +
          'Day MTM beside it is today alone, and includes what today realised.'
        }
      >
        NET MTM
      </span>
      {unrealised.kind === 'UNMARKED' ? (
        /*
          Not a zero. Nothing in the book has a price yet, and printing
          "+₹0" would be a valuation of a position nobody can value.
        */
        <span
          className="tr-sval num text-muted-foreground"
          data-testid="open-mtm-value"
        >
          —
        </span>
      ) : (
        <span
          className={cn(
            'tr-sval num',
            unrealised.value < 0 ? 'text-destructive' : 'text-success',
          )}
          data-testid="open-mtm-value"
        >
          {money(unrealised.value)}
          {unrealised.kind === 'PARTIAL' && (
            /*
              The count, not an asterisk. A header that silently covers 12
              of 15 legs is the understatement `basketMtm`'s union exists
              to prevent, and the missing ones are usually the far wings.
            */
            <span
              className="ml-1 text-[10px] font-semibold text-warning"
              title={`${unrealised.unmarked} leg${unrealised.unmarked === 1 ? '' : 's'} have no price yet, so they are not in this figure`}
              data-testid="open-mtm-partial"
            >
              {unrealised.marked}/{unrealised.marked + unrealised.unmarked}
            </span>
          )}
        </span>
      )}
    </div>
  )
})
